import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Hammer, 
  Building, 
  Wrench, 
  Shield, 
  Truck,
  Users,
  CheckCircle,
  MapPin,
  Clock,
  Award,
  Target,
  Zap,
  Settings
} from "lucide-react";

const ContractingPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    animate: { transition: { staggerChildren: 0.1 } }
  };

  const services = [
    {
      icon: <Building className="w-8 h-8" />,
      title: "Sustainable Development",
      description: "Eco-friendly construction projects that blend cutting-edge technology with environmental stewardship."
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Property Rehabilitation",
      description: "Complete renovation and restoration services that transform distressed properties into profitable assets."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Infrastructure Building",
      description: "Large-scale infrastructure projects including roads, utilities, and community development initiatives."
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Custom Construction",
      description: "Bespoke construction solutions for luxury residential, commercial, and specialized industrial projects."
    }
  ];

  const metrics = [
    { number: "$47M", label: "Projects Completed" },
    { number: "234", label: "Properties Built" },
    { number: "98%", label: "Client Satisfaction" },
    { number: "15", label: "Cities Operating" }
  ];

  const projects = [
    {
      title: "Eco-Retreat Paradise",
      location: "Costa Rica",
      value: "$3.2M",
      type: "Sustainable Resort",
      status: "Completed",
      image: "https://images.unsplash.com/photo-1489515229412-1f3a8f08dc34",
      description: "50-unit eco-luxury retreat with solar power and rainwater harvesting"
    },
    {
      title: "Urban Apartment Complex",
      location: "Austin, TX",
      value: "$8.7M",
      type: "Residential",
      status: "In Progress",
      image: "https://images.pexels.com/photos/5845904/pexels-photo-5845904.jpeg",
      description: "120-unit luxury apartment complex with smart home integration"
    },
    {
      title: "Community Wellness Center",
      location: "Detroit, MI",
      value: "$2.1M",
      type: "Non-Profit",
      status: "Planning",
      image: "https://images.unsplash.com/photo-1608613304899-ea8098577e38",
      description: "Holistic wellness facility serving underserved communities"
    }
  ];

  const capabilities = [
    {
      category: "Construction Types",
      items: ["Residential", "Commercial", "Industrial", "Infrastructure"],
      icon: <Building className="w-6 h-6" />
    },
    {
      category: "Specializations",
      items: ["Eco-Builds", "Smart Homes", "Luxury Resorts", "Wellness Centers"],
      icon: <Zap className="w-6 h-6" />
    },
    {
      category: "Services",
      items: ["Design-Build", "Renovation", "Project Management", "Consulting"],
      icon: <Settings className="w-6 h-6" />
    },
    {
      category: "Regions",
      items: ["North America", "Central America", "Caribbean", "Expanding"],
      icon: <MapPin className="w-6 h-6" />
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Sacred Geometry Background */}
        <div className="absolute inset-0 opacity-8">
          <motion.div 
            className="absolute inset-0"
            animate={{ rotate: 360 }}
            transition={{ duration: 160, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='140' height='140' viewBox='0 0 140 140' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23f59e0b' stroke-opacity='0.1' stroke-width='1'%3E%3Crect x='10' y='10' width='120' height='120'/%3E%3Crect x='25' y='25' width='90' height='90'/%3E%3Crect x='40' y='40' width='60' height='60'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '140px 140px'
            }}
          />
        </div>

        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1489515229412-1f3a8f08dc34"
            alt="Construction and development"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-orange-900/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div 
              className="flex justify-center mb-8"
              variants={fadeInUp}
            >
              <div className="w-24 h-24 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center shadow-2xl shadow-orange-400/50">
                <Hammer className="w-12 h-12 text-black" />
              </div>
            </motion.div>

            <motion.h1 
              className="text-6xl md:text-8xl font-bold mb-6"
              variants={fadeInUp}
            >
              Nhalege <span className="text-orange-400">Contracting</span>
            </motion.h1>
            
            <motion.p 
              className="text-2xl md:text-3xl text-orange-400 mb-8 font-light"
              variants={fadeInUp}
            >
              Build the Future
            </motion.p>
            
            <motion.p 
              className="text-xl text-white/80 max-w-4xl mx-auto mb-12 leading-relaxed"
              variants={fadeInUp}
            >
              Revolutionary construction and development force that builds sustainable infrastructure, 
              transforms communities, and creates lasting value through precision craftsmanship and visionary design.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center"
              variants={fadeInUp}
            >
              <button className="group bg-gradient-to-r from-orange-400 to-red-500 text-black px-12 py-4 text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-orange-400/40 transition-all duration-300 hover:scale-105">
                <div className="flex items-center space-x-2">
                  <span>Request Project Quote</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
              
              <button className="group border-2 border-orange-400 text-orange-400 px-12 py-4 text-lg font-bold rounded-full hover:bg-orange-400 hover:text-black transition-all duration-300 hover:scale-105">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>Join Build Team</span>
                </div>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Project Metrics Section */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {metrics.map((metric, index) => (
              <motion.div 
                key={metric.label}
                className="text-center"
                variants={fadeInUp}
              >
                <div className="text-4xl md:text-5xl font-bold text-orange-400 mb-2">
                  {metric.number}
                </div>
                <div className="text-white/70 text-sm font-medium tracking-wider uppercase">
                  {metric.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Core Mission Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.pexels.com/photos/5845904/pexels-photo-5845904.jpeg"
            alt="Construction equipment"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-5xl md:text-6xl font-bold mb-8">
                Core <span className="text-orange-400">Mission</span>
              </h2>
              <div className="space-y-6 text-lg text-white/80 leading-relaxed">
                <p>
                  Nhalege Contracting operates as the physical manifestation of our empire's vision, 
                  building the infrastructure that supports generational wealth and community transformation.
                </p>
                <p>
                  We reject cookie-cutter construction and instead focus on sustainable, innovative 
                  building solutions that appreciate in value while minimizing environmental impact.
                </p>
                <p>
                  Our approach combines <span className="text-orange-400 font-semibold">
                  cutting-edge construction technology, sustainable practices, and strategic project selection</span> 
                  to create lasting value for our empire and our clients.
                </p>
              </div>
              
              <div className="mt-10 p-8 bg-gradient-to-br from-orange-400/10 to-transparent border border-orange-400/20 rounded-2xl">
                <h3 className="text-2xl font-bold text-orange-400 mb-4">Construction Philosophy</h3>
                <ul className="space-y-3 text-white/80">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <span>Sustainable building practices and materials</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <span>Smart technology integration in all projects</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <span>Community-focused development approach</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <span>Long-term value creation over quick profits</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              variants={fadeInUp}
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1608613304899-ea8098577e38"
                  alt="Skilled construction"
                  className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-3xl"></div>
                
                {/* Floating Project Card */}
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gray-900 to-black border border-orange-400/30 rounded-2xl p-6 backdrop-blur-lg">
                  <div className="flex items-center space-x-4">
                    <CheckCircle className="w-8 h-8 text-orange-400" />
                    <div>
                      <div className="text-2xl font-bold text-white">234</div>
                      <div className="text-sm text-white/60">Projects Built</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Construction <span className="text-orange-400">Services</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Comprehensive building solutions from concept to completion
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="group bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8 hover:border-orange-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-black">{service.icon}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-orange-400 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-white/70 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-10">
          <motion.div 
            animate={{ y: [0, 50, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='90' height='90' viewBox='0 0 90 90' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23f59e0b' stroke-opacity='0.1' stroke-width='1'%3E%3Crect x='5' y='5' width='80' height='80'/%3E%3Crect x='20' y='20' width='50' height='50'/%3E%3Crect x='35' y='35' width='20' height='20'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '90px 90px'
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Featured <span className="text-orange-400">Projects</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Transformative developments that build communities and create lasting value
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {projects.map((project, index) => (
              <motion.div
                key={project.title}
                className="group bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-lg border border-orange-400/20 rounded-3xl overflow-hidden hover:border-orange-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="aspect-video relative">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute top-4 right-4">
                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                      project.status === 'Completed' ? 'bg-green-400 text-black' :
                      project.status === 'In Progress' ? 'bg-orange-400 text-black' :
                      'bg-blue-400 text-black'
                    }`}>
                      {project.status}
                    </div>
                  </div>
                  <div className="absolute top-4 left-4">
                    <div className="bg-black/60 backdrop-blur-lg text-white px-3 py-1 rounded-full text-sm">
                      {project.location}
                    </div>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-300">
                    {project.title}
                  </h3>
                  
                  <div className="text-orange-400 font-semibold mb-4">{project.type}</div>
                  
                  <p className="text-white/70 mb-6 leading-relaxed">
                    {project.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-orange-400">{project.value}</div>
                    <button className="bg-gradient-to-r from-orange-400 to-red-500 text-black px-6 py-2 font-bold rounded-full hover:shadow-lg hover:shadow-orange-400/30 transition-all duration-300 hover:scale-105">
                      View Details
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Our <span className="text-orange-400">Capabilities</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Comprehensive construction expertise across all sectors and regions
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.category}
                className="group text-center"
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center group-hover:shadow-lg group-hover:shadow-orange-400/30 transition-all duration-300">
                  <span className="text-black">{capability.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-orange-400 transition-colors duration-300">
                  {capability.category}
                </h3>
                <ul className="space-y-2">
                  {capability.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="text-white/70 text-sm">
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-orange-400/10 via-black to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="text-5xl md:text-6xl font-bold mb-8"
              variants={fadeInUp}
            >
              Build Your <span className="text-orange-400">Vision</span>
            </motion.h2>
            
            <motion.p 
              className="text-xl text-white/80 mb-12"
              variants={fadeInUp}
            >
              Ready to transform your project into reality? Partner with the construction force that builds the future.
            </motion.p>
            
            <motion.div 
              className="space-y-8"
              variants={fadeInUp}
            >
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-orange-400/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-orange-400 mb-6">Project Consultation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    placeholder="Project Type"
                    className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-orange-400"
                  />
                  <input 
                    type="text" 
                    placeholder="Budget Range"
                    className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-orange-400"
                  />
                  <input 
                    type="text" 
                    placeholder="Location"
                    className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-orange-400"
                  />
                  <input 
                    type="text" 
                    placeholder="Timeline"
                    className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-orange-400"
                  />
                </div>
                <textarea 
                  placeholder="Project Description"
                  rows="4"
                  className="w-full mt-4 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-orange-400"
                ></textarea>
                <button className="w-full mt-6 bg-gradient-to-r from-orange-400 to-red-500 text-black px-8 py-4 text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-orange-400/40 transition-all duration-300 hover:scale-105">
                  Get Project Quote
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 bg-gray-900 border-t border-orange-400/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link 
              to="/media"
              className="group flex items-center space-x-2 text-white/70 hover:text-orange-400 transition-colors duration-300 mb-4 md:mb-0"
            >
              <ArrowRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
              <span>Previous: Media & Education</span>
            </Link>
            
            <Link 
              to="/"
              className="text-orange-400 font-semibold hover:text-white transition-colors duration-300"
            >
              ⬟ Empire Overview
            </Link>
            
            <Link 
              to="/real-estate"
              className="group flex items-center space-x-2 text-white/70 hover:text-orange-400 transition-colors duration-300 mt-4 md:mt-0"
            >
              <span>Next: Real Estate</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContractingPage;